import React, { useState, useEffect, useRef, useMemo, Fragment, useCallback } from 'react';
import { Dialog as HeadlessUIDialog, Transition } from '@headlessui/react';
import logoAws from '../assets/icons-new-bucket/aws.svg';
import logoGoogle from '../assets/cloud-providers/google-cloud-icon.svg';
import logoAzure from '../assets/icons-new-bucket/azure.svg';
import CommonFields from '../components/storage/CommonFields';
import StorageTypeFields, {
  S3FieldsType,
  GCSFieldsType,
  AzureFieldsType,
} from '../components/storage/StorageTypeFields';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  Cell,
  ReferenceLine,
} from 'recharts';
import { format } from 'date-fns';
import {
  Typography,
  IconButton,
  Tooltip as MuiTooltip,
  Button,
  CircularProgress,
  Popover,
  Box,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
// Removed back arrow usage in header
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { ApiTypes, HttpError } from '@flashbacktech/flashbackclient/api';
import { useApi } from '../contexts/ApiContext';
import { useAuth, useSelectedWorkspace } from '../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { useWithTokenRefresh } from '../utils/api';
import { toastError, toastSuccess } from '../utils/toast';
import { buildTimeSeriesParams, type DatePreset } from '../utils/v2StatsHelpers';

// Fonction pour formater les nombres avec des suffixes (K, M, G, T)
const formatNumber = (num: number): string => {
  if (num >= 1000000000000) return (num / 1000000000000).toFixed(2) + 'T';
  if (num >= 1000000000) return (num / 1000000000).toFixed(2) + 'G';
  if (num >= 1000000) return (num / 1000000).toFixed(2) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(2) + 'K';
  return num.toString();
};

// Fonction pour formater les octets en taille lisible
const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];

  // S'assurer que nous avons un nombre positif
  const absoluteBytes = Math.abs(bytes);

  // Trouver l'unité la plus appropriée
  const i = absoluteBytes > 0 ? Math.floor(Math.log(absoluteBytes) / Math.log(k)) : 0;

  // Limiter l'indice à la taille du tableau
  const index = Math.min(i, sizes.length - 1);

  // Formater avec 2 décimales et supprimer les zéros inutiles
  const value = parseFloat((bytes / Math.pow(k, index)).toFixed(2));

  // Ajouter le signe si nécessaire
  const sign = bytes < 0 ? '-' : '';

  return `${sign}${value} ${sizes[index]}`;
};

/**
 * Arrondit une valeur max vers le haut pour créer un domaine d'axe Y propre
 * GARANTIT un résultat divisible par 5 pour afficher 5 paliers égaux
 * S'adapte automatiquement à toutes les plages de valeurs
 * 
 * Exemples avec 5 paliers :
 * - 0.187 → 0.2 → Paliers: [0, 0.04, 0.08, 0.12, 0.16, 0.2]
 * - 45 → 50 → Paliers: [0, 10, 20, 30, 40, 50]
 * - 87 → 100 → Paliers: [0, 20, 40, 60, 80, 100]
 * - 190 → 200 → Paliers: [0, 40, 80, 120, 160, 200]
 * - 1234 → 1500 → Paliers: [0, 300, 600, 900, 1200, 1500]
 */
const roundToNiceDomain = (maxValue: number): number => {
  // Gérer le cas où maxValue est 0 ou très proche de 0
  if (maxValue === 0) return 0.5;
  if (maxValue < 0.001) return 0.005; // Pour très petites valeurs
  
  // Trouver l'ordre de grandeur (puissance de 10)
  const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));
  
  // Paliers possibles - TOUS divisibles par 5
  // On commence par des fractions pour gérer les petites valeurs
  const possibleSteps = [
    magnitude * 0.5,   // 0.05, 0.5, 5, 50, 500
    magnitude * 1,     // 0.1, 1, 10, 100, 1000
    magnitude * 1.5,   // 0.15, 1.5, 15, 150, 1500
    magnitude * 2,     // 0.2, 2, 20, 200, 2000
    magnitude * 2.5,   // 0.25, 2.5, 25, 250, 2500
    magnitude * 5,     // 0.5, 5, 50, 500, 5000
    magnitude * 10,    // 1, 10, 100, 1000, 10000
  ];
  
  // Trouver le premier palier >= maxValue
  for (const step of possibleSteps) {
    if (step >= maxValue) {
      return step;
    }
  }
  
  // Fallback (ne devrait jamais arriver)
  return magnitude * 10;
};

/**
 * Arrondit une valeur en bytes pour produire un chiffre rond après formatBytes()
 * Exemples:
 * - 187654321 bytes (~179 MB) → 209715200 bytes (200 MB)
 * - 5432123 bytes (~5.18 MB) → 5767168 bytes (5.5 MB)
 * - 2345678901 bytes (~2.18 GB) → 2684354560 bytes (2.5 GB)
 */
const roundToNiceDomainBytes = (maxValueBytes: number): number => {
  if (maxValueBytes === 0) return 1024 * 1024; // 1 MB par défaut
  if (maxValueBytes < 0) return roundToNiceDomainBytes(Math.abs(maxValueBytes));
  
  const k = 1024;
  const absoluteBytes = Math.abs(maxValueBytes);
  
  // Trouver l'unité appropriée
  const i = absoluteBytes > 0 ? Math.floor(Math.log(absoluteBytes) / Math.log(k)) : 0;
  const index = Math.min(i, 5); // B, KB, MB, GB, TB, PB
  
  // Convertir en cette unité
  const valueInUnit = maxValueBytes / Math.pow(k, index);
  
  // Arrondir avec roundToNiceDomain
  const roundedInUnit = roundToNiceDomain(valueInUnit);
  
  // Reconvertir en bytes
  return roundedInUnit * Math.pow(k, index);
};

function StorageUnitDetails() {
  const { apiClient } = useApi();
  const { isAuthenticated, authState } = useAuth();
  const navigate = useNavigate();
  const { unitId } = useParams<{ unitId: string }>();
  const withTokenRefresh = useWithTokenRefresh();
  const [unit, setUnit] = useState<ApiTypes.StorageUnit | null>(null);
  const [associatedRepos, setAssociatedRepos] = useState<ApiTypes.StorageRepoWithBuckets[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingRepos, setLoadingRepos] = useState(true);
  const [nodeStats, setNodeStats] = useState<
    Record<string, { nodeStatus: string; latency_ms: number; lastUpdated: Date }>
  >({});
  const [loadingNodeStats, setLoadingNodeStats] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [openEditUnit, setOpenEditUnit] = useState(false);
  const [newUnit, setNewUnit] = useState<Partial<ApiTypes.StorageUnit> & { apiKeyId?: string }>({});
  const [availableApiKeys, setAvailableApiKeys] = useState<ApiTypes.ProviderApiKeyDTO[]>([]);
  const [isLoadingApiKeys, setIsLoadingApiKeys] = useState(false);

  // Map StorageType to provider id for API key filter (backend uses AWS, GCP, AZURE)
  const storageTypeToProvider = (st: ApiTypes.StorageType | undefined): string | undefined => {
    if (!st) return undefined;
    switch (st) {
      case ApiTypes.StorageType.S3: return 'AWS';
      case ApiTypes.StorageType.GCS: return 'GCP';
      case ApiTypes.StorageType.BLOB: return 'AZURE';
      default: return undefined;
    }
  };

  // Load available API keys (filtered by storage capability and selected provider when set)
  const loadApiKeys = async () => {
    try {
      setIsLoadingApiKeys(true);
      const targetWorkspaceId = (unit as any)?.workspaceId || (unit as any)?.workspace_id;
      const provider = storageTypeToProvider(newUnit.storageType ?? unit?.storageType);
      const response = await apiClient.getApiKeys({
        workspaceId: targetWorkspaceId,
        capability: ApiTypes.ProviderCapability.Storage,
        ...(provider ? { provider } : {}),
      });
      if (response && response.success) {
        setAvailableApiKeys(response.apiKeys || []);
      }
    } catch (error) {
      console.error('Failed to load API keys:', error);
    } finally {
      setIsLoadingApiKeys(false);
    }
  };

  useEffect(() => {
    if (openEditUnit && unit) {
      loadApiKeys();
    }
  }, [openEditUnit, unit, newUnit.storageType]);
  const [s3Fields, setS3Fields] = useState<S3FieldsType>({
    accessKey: '',
    secret: '',
    region: '',
    bucket: '',
    endpoint: '',
  });
  const [gcsFields, setGcsFields] = useState<GCSFieldsType>({
    clientEmail: '',
    privateKey: '',
    bucket: '',
    endpoint: '',
  });
  const [azureFields, setAzureFields] = useState<AzureFieldsType>({
    storageAccount: '',
    container: '',
    accessKey: '',
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  
  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // États pour les repos
  const [repoStartIndex, setRepoStartIndex] = useState(0);
  const [visibleReposCount, setVisibleReposCount] = useState(2); // Nombre de repos visibles à la fois (fixé à 2)
  // Interface pour les données historiques
  interface HistoricalDataPoint {
    date: string;
    value: number;
  }

  // Interface pour les statistiques
  interface UnitStats {
    calls: { value: number; change: number; history: HistoricalDataPoint[] };
    latency: { value: number; change: number; history: HistoricalDataPoint[] };
    download: { value: number; history: HistoricalDataPoint[] };
    upload: { value: number; history: HistoricalDataPoint[] };
    storageChange: { value: number; change: number; history: HistoricalDataPoint[] };
  }

  const [stats, setStats] = useState<UnitStats>({
    calls: { value: 0, change: 0, history: [] },
    latency: { value: 0, change: 0, history: [] },
    download: { value: 0, history: [] },
    upload: { value: 0, history: [] },
    storageChange: { value: 0, change: 0, history: [] },
  });

  // Date picker state
  const [datePickerOpen, setDatePickerOpen] = useState<boolean>(false);
  const [dateAnchorEl, setDateAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<string>('Last 30 days');
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(() => {
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    yesterday.setHours(23, 59, 59, 999);
    const start = new Date(yesterday.getTime() - 29 * 24 * 60 * 60 * 1000);
    start.setHours(0, 0, 0, 0);
    return start;
  });
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(() => {
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    yesterday.setHours(23, 59, 59, 999);
    return yesterday;
  });
  const [calendarBaseDate, setCalendarBaseDate] = useState<Date>(new Date());

  // Référence pour détecter les changements de largeur d'écran
  const containerRef = useRef<HTMLDivElement>(null);

  const presetToV2Window = (preset: string): DatePreset => {
    switch (preset) {
      case 'Last hour':
      case 'Last 24 hours': return '24h';
      case 'Last 7 days': return '7d';
      case 'Last 30 days': return '30d';
      case 'Last 365 days': return '365d';
      case 'All time': return '365d';
      default: return '30d';
    }
  };

  const fetchUnitDetailedStats = useCallback(
    async (unitId: string) => {
      setLoadingStats(true);
      setLoadingNodeStatsMinute(true);

      try {
        const v2Window = presetToV2Window(selectedPreset);
        const params = buildTimeSeriesParams(null, null, v2Window, { bucketId: unitId });

        const v2Response = await withTokenRefresh(() =>
          apiClient.getStorageGatewayStats(params as any)
        );

        if (v2Response.success && v2Response.series && v2Response.series.length > 0) {
          const series = v2Response.series;

          let totalCalls = 0, totalUpl = 0, totalDwl = 0, totalStor = 0, sumLat = 0, latCnt = 0;
          series.forEach((point: any) => {
            totalCalls += point.requests;
            totalUpl += point.uplBytes;
            totalDwl += point.dwlBytes;
            totalStor += point.sizeChange;
            if (point.latencyMs > 0) { sumLat += point.latencyMs; latCnt++; }
          });

          const avgLatency = latCnt > 0 ? sumLat / latCnt : 0;

          const toHistory = (extractor: (p: any) => number): HistoricalDataPoint[] =>
            series.map((point: any) => ({
              date: new Date(point.ts * 1000).toISOString(),
              value: extractor(point),
            }));

          setStats({
            calls: {
              value: totalCalls,
              change: 0,
              history: toHistory((p) => p.requests),
            },
            latency: {
              value: avgLatency,
              change: 0,
              history: toHistory((p) => p.latencyMs),
            },
            download: {
              value: totalDwl / (1024 * 1024 * 1024),
              history: toHistory((p) => p.dwlBytes / (1024 * 1024 * 1024)),
            },
            upload: {
              value: totalUpl / (1024 * 1024 * 1024),
              history: toHistory((p) => p.uplBytes / (1024 * 1024 * 1024)),
            },
            storageChange: {
              value: totalStor,
              change: 0,
              history: toHistory((p) => p.sizeChange / (1024 * 1024 * 1024)),
            },
          });
        } else {
          setStats({
            calls: { value: 0, change: 0, history: [] },
            latency: { value: 0, change: 0, history: [] },
            download: { value: 0, history: [] },
            upload: { value: 0, history: [] },
            storageChange: { value: 0, change: 0, history: [] },
          });
        }

        // Fetch bridge node stats using apiClient.getBucketNodeStats (V1 — kept as-is)
        try {
          console.log('[StorageUnitDetails] Fetching bridge node stats for unitId:', unitId);
          const specificDate = new Date();
          
          const response = await withTokenRefresh(() =>
            apiClient.getBucketNodeStats(unitId, { day: specificDate })
          );
          
          console.log('[StorageUnitDetails] Bridge node stats API response:', response);

          if (response && response.nodeStats && Array.isArray(response.nodeStats)) {
            console.log('[StorageUnitDetails] Bridge node stats array length:', response.nodeStats.length);
            const mappedNodes = response.nodeStats.map((node: any, index: number) => {
              console.log(`[StorageUnitDetails] Processing bridge node ${index}:`, node);
              return {
                nodeId: node.ip || '',
                unitId: unitId,
                nodeStatus: node.node_status || 'UNKNOWN',
                lastUpdated: new Date(node.last_updated || Date.now()),
                latency_ms: node.avg_latency_ms || 0,
                host: node.host || '',
                version: node.version || '',
                perc_uptime: node.perc_uptime || 0,
              };
            });
            console.log('[StorageUnitDetails] Mapped bridge nodes:', mappedNodes);
            setNodeStatsMinute(mappedNodes);
          } else {
            console.log('[StorageUnitDetails] No nodeStats in response or invalid format');
            setNodeStatsMinute([]);
          }
          console.log('[StorageUnitDetails] Setting loadingNodeStatsMinute to false');
          setLoadingNodeStatsMinute(false);
        } catch (nodeError) {
          console.error('[StorageUnitDetails] Error fetching bridge node stats:', nodeError);
          setNodeStatsMinute([]);
          setLoadingNodeStatsMinute(false);
        }
      } catch (error) {
        // Set default values on error
        setStats({
          calls: { value: 0, change: 0, history: [] },
          latency: { value: 0, change: 0, history: [] },
          download: { value: 0, history: [] },
          upload: { value: 0, history: [] },
          storageChange: { value: 0, change: 0, history: [] },
        });
        // Also reset node stats loading on outer error
        setNodeStatsMinute([]);
        setLoadingNodeStatsMinute(false);
      } finally {
        setLoadingStats(false);
      }
    },
    [selectedPreset, withTokenRefresh, apiClient, authState]
  );

  // Debug: Log associatedRepos state changes
  useEffect(() => { /* Empty */ }, [associatedRepos, loadingRepos]);

  // Update selected dates whenever a preset is chosen
  useEffect(() => {
    const { start, end } = getRangeForPreset(selectedPreset);
    setSelectedStartDate(start);
    setSelectedEndDate(end);
  }, [selectedPreset]);

  // Reload stats when date preset changes
  useEffect(() => {
    if (unitId) {
      fetchUnitDetailedStats(unitId);
    }
  }, [unitId, selectedPreset]);

  // Réinitialiser l'index de départ si nécessaire quand le nombre de repos change
  useEffect(() => {
    if (repoStartIndex > 0 && repoStartIndex + visibleReposCount > associatedRepos.length) {
      setRepoStartIndex(Math.max(0, associatedRepos.length - visibleReposCount));
    }
  }, [associatedRepos.length, repoStartIndex, visibleReposCount]);

  // Interface pour les données de node
  interface NodeStatsMinuteData {
    nodeId: string;
    unitId: string;
    nodeStatus: string;
    lastUpdated: Date;
    latency_ms: number;
    host?: string;
    version?: string;
    perc_uptime?: number;
  }

  // État pour stocker les statistiques des nodes
  const [nodeStatsMinute, setNodeStatsMinute] = useState<NodeStatsMinuteData[]>([]);
  const [loadingNodeStatsMinute, setLoadingNodeStatsMinute] = useState(true);
  
  // Use centralized workspace hook
  const selectedWorkspaceId = useSelectedWorkspace();

  const getWorkspaceIdToUse = () => {
    if (!selectedWorkspaceId || selectedWorkspaceId === 'ALL') {
      throw new Error('Please select a workspace');
    }
    return selectedWorkspaceId;
  };

  // Memoize unit IDs to prevent unnecessary re-renders
  const unitIdsToFetch = useMemo(() => {
    const unitIds = new Set<string>();

    // Add the current unit ID
    if (unitId) {
      unitIds.add(unitId);
    }

    // Add unit IDs from associated repos
    // Note: repos from new API structure don't have 'buckets' property
    associatedRepos.forEach((repo) => {
      const repoAny = repo as any;
      if (repoAny.buckets && Array.isArray(repoAny.buckets)) {
        repoAny.buckets.forEach((bucket: any) => {
          if (bucket.bucketId) {
            unitIds.add(bucket.bucketId);
          }
        });
      }
    });

    return Array.from(unitIds);
  }, [unitId, associatedRepos.map((repo) => repo.id).join(',')]);

  // Label lisible pour le type de stockage
  const storageTypeLabel = useMemo(() => {
    if (!unit) return '';
    switch (unit.storageType) {
      case ApiTypes.StorageType.S3:
        return 's3';
      case ApiTypes.StorageType.GCS:
        return 'gcs';
      case ApiTypes.StorageType.BLOB:
        return 'blob';
      default:
        return (unit.storageType as any)?.toString?.().toLowerCase?.() || '';
    }
  }, [unit]);

  // Single useEffect to fetch all node stats
  useEffect(() => {
    const fetchAllNodeStats = async () => {
      if (!isAuthenticated || unitIdsToFetch.length === 0) return;

      setLoadingNodeStats(true);

      try {
        // Fetch status per bucket id and aggregate
        const results = await Promise.all(
          unitIdsToFetch.map((id) => withTokenRefresh(() => apiClient.getStorageBucketStatus(id)))
        );

        const statsMap: Record<
          string,
          { nodeStatus: string; latency_ms: number; lastUpdated: Date }
        > = {};

        results.forEach((res) => {
          if (!res) return;
          const bucketId = (res as any).bucketId;
          const nodes: any[] = (res as any).nodeStatus || [];
          if (!bucketId) return;
          if (nodes.length === 0) return;

          // Determine overall status and average latency
          const hasOnline = nodes.some((n) => (n.status || n.node_status) === 'ONLINE');
          const avgLatency =
            nodes.reduce((s, n) => s + (Number(n.latency_ms) || 0), 0) / nodes.length;
          const latestUpdated = nodes
            .map((n) => new Date(n.lastUpdated || n.last_updated || Date.now()).getTime())
            .reduce((a, b) => Math.max(a, b), 0);

          statsMap[bucketId] = {
            nodeStatus: hasOnline ? 'ONLINE' : 'OFFLINE',
            latency_ms: avgLatency || 0,
            lastUpdated: new Date(latestUpdated),
          };
        });

        setNodeStats(statsMap);
      } catch (error) { /* Ignore errors */ } finally {
        setLoadingNodeStats(false);
      }
    };

    fetchAllNodeStats();

    // Refresh stats every 30 seconds
    const intervalId = setInterval(fetchAllNodeStats, 30000);

    return () => clearInterval(intervalId);
  }, [apiClient, unitIdsToFetch, isAuthenticated, withTokenRefresh]);

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      if (!isAuthenticated) {
        navigate('/');
        return;
      }

      if (!unitId) {
        navigate('/cloud-storage/buckets');
        return;
      }

      try {
        setLoading(true);
        setLoadingRepos(true);

        // Récupérer les unités
        const unitsResponse = await withTokenRefresh(apiClient.getStorageBuckets.bind(apiClient));

        let foundUnit = null;
        if (isMounted) {
          foundUnit = unitsResponse.buckets.find((unit) => unit.id === unitId);
          if (foundUnit) {
            setUnit(foundUnit);
          }
          setLoading(false);
        }

        // Récupérer les repos associés
        if (foundUnit) {
          // Check if the bucket already contains repos array (new API structure)
          const foundUnitAny = foundUnit as any;
          if (
            foundUnitAny.repos &&
            Array.isArray(foundUnitAny.repos) &&
            foundUnitAny.repos.length > 0
          ) {
            // Use repos directly from bucket object
            setAssociatedRepos(foundUnitAny.repos);
            setLoadingRepos(false);
          } else {
            // Fallback: fetch all repos and filter (old system)
            const reposResponse = await withTokenRefresh(apiClient.getStorageRepos.bind(apiClient));

            if (isMounted) {
              // Log de tous les repos pour debugging

              // Filtrer les repos qui utilisent cette unité
              const filteredRepos = reposResponse.repos.filter((repo) => {
                return repo.buckets.some((repoUnit) => {
                  const repoUnitAny = repoUnit as any;
                  // Check all possible field names (including bucket.id from API spec)
                  const bucketId =
                    repoUnitAny.bucketId ||
                    repoUnitAny.bucket?.id || // ← Important: check nested bucket.id
                    repoUnitAny.unitId ||
                    repoUnitAny.bucket_id ||
                    repoUnitAny.unit_id ||
                    repoUnitAny.storageUnitId ||
                    repoUnitAny.storage_unit_id ||
                    repoUnitAny.id;

                  const matches = bucketId === unitId;
                  if (matches) { /* Empty */ }
                  return matches;
                });
              });

              setAssociatedRepos(filteredRepos);
              setLoadingRepos(false);
            }
          }

          // Récupérer les statistiques détaillées via l'API
          try {
            // Utiliser notre nouvelle fonction pour récupérer les statistiques détaillées
            if (isMounted) {
              fetchUnitDetailedStats(unitId);
            }
          } catch (statsError) {
            if (isMounted) {
              setLoadingStats(false);
            }
          }
        } else {
          setLoadingRepos(false);
          setLoadingStats(false);
        }
      } catch (error) {
        if (isMounted) {
          setLoading(false);
          setLoadingRepos(false);
          if (error instanceof HttpError && error.status === 404) {
            toastError('Storage unit not found');
          } else {
            toastError(
              `Failed to load storage unit details: ${
                error instanceof Error ? error.message : 'Unknown error'
              }`
            );
          }
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [apiClient, isAuthenticated, navigate, unitId, withTokenRefresh]);

  // Fonction pour gérer la copie dans le presse-papiers
  const handleCopy = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toastSuccess('Copied to clipboard!');
      })
      .catch((error) => {
        toastError('Failed to copy to clipboard');
      });
  };

  // Fonction pour gérer l'édition de l'unité
  const handleEditUnit = async () => {
    if (!unit || !unitId) return;

    try {
      setLoading(true);

      // Valider les champs
      const errors: Record<string, string> = {};
      if (!newUnit.name) errors.name = 'Name is required';

      // Valider les champs spécifiques au type de stockage
      if (!newUnit.apiKeyId) {
        if (newUnit.storageType === ApiTypes.StorageType.S3) {
          if (!s3Fields.accessKey) errors.accessKey = 'Access Key is required';
          if (!s3Fields.secret) errors.secret = 'Secret Key is required';
          if (!s3Fields.bucket) errors.bucket = 'Bucket is required';
        } else if (newUnit.storageType === ApiTypes.StorageType.GCS) {
          if (!gcsFields.clientEmail) errors.clientEmail = 'Client Email is required';
          if (!gcsFields.privateKey) errors.privateKey = 'Private Key is required';
          if (!gcsFields.bucket) errors.bucket = 'Bucket is required';
        } else if (newUnit.storageType === ApiTypes.StorageType.BLOB) {
          if (!azureFields.storageAccount) errors.storageAccount = 'Storage Account is required';
          if (!azureFields.accessKey) errors.accessKey = 'Access Key is required';
          if (!azureFields.container) errors.container = 'Container is required';
        }
      } else {
        // If apiKeyId is selected, we still need the bucket name / container
        if (newUnit.storageType === ApiTypes.StorageType.S3 && !s3Fields.bucket) errors.bucket = 'Bucket is required';
        if (newUnit.storageType === ApiTypes.StorageType.GCS && !gcsFields.bucket) errors.bucket = 'Bucket is required';
        if (newUnit.storageType === ApiTypes.StorageType.BLOB && !azureFields.container) errors.container = 'Container is required';
      }

      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        setLoading(false);
        return;
      }

      // Préparer les données à envoyer
      const getBucket = (storageType: ApiTypes.StorageType) => {
        if (storageType === ApiTypes.StorageType.S3) return s3Fields.bucket;
        if (storageType === ApiTypes.StorageType.GCS) return gcsFields.bucket;
        if (storageType === ApiTypes.StorageType.BLOB) return azureFields.container;
        return '';
      };

      const getKey = (storageType: ApiTypes.StorageType) => {
        if (storageType === ApiTypes.StorageType.S3) return s3Fields.accessKey;
        if (storageType === ApiTypes.StorageType.GCS) return gcsFields.clientEmail;
        if (storageType === ApiTypes.StorageType.BLOB) return azureFields.accessKey;
        return '';
      };

      const getSecret = (storageType: ApiTypes.StorageType) => {
        if (storageType === ApiTypes.StorageType.S3) return s3Fields.secret;
        if (storageType === ApiTypes.StorageType.GCS) return gcsFields.privateKey;
        if (storageType === ApiTypes.StorageType.BLOB) return '';
        return '';
      };

      // Get workspaceId from unit
      const workspaceIdToUse = (unit as any)?.workspaceId || (unit as any)?.workspace_id;
      if (!workspaceIdToUse) {
        throw new Error('Workspace ID is required to update unit');
      }

      const updatedUnit: any = {
        name: newUnit.name || '',
        bucket: getBucket(newUnit.storageType || ApiTypes.StorageType.S3),
        storageType: newUnit.storageType || ApiTypes.StorageType.S3,
        workspaceId: workspaceIdToUse,
        region: s3Fields.region || '',
        endpoint:
          newUnit.storageType === ApiTypes.StorageType.S3
            ? s3Fields.endpoint
            : newUnit.storageType === ApiTypes.StorageType.GCS
            ? gcsFields.endpoint
            : '',
      };

      if (newUnit.apiKeyId) {
        updatedUnit.apiKeyUuid = newUnit.apiKeyId;
      } else {
        updatedUnit.key = getKey(newUnit.storageType || ApiTypes.StorageType.S3);
        updatedUnit.secret = getSecret(newUnit.storageType || ApiTypes.StorageType.S3);
      }

      // Envoyer la requête d'édition
      const response = await withTokenRefresh(() =>
        apiClient.updateStorageBucket(unitId, updatedUnit)
      );

      if (response) {
        toastSuccess('Storage unit updated successfully');
        setOpenEditUnit(false);

        // Mettre à jour l'état local avec les nouvelles données
        // Recharger la page pour avoir les informations à jour
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (error) {
      if (error instanceof HttpError) {
        toastError(`Error: ${error.message}`);
      } else {
        toastError('An error occurred while updating the storage unit');
      }
    } finally {
      setLoading(false);
    }
  };

  // Date picker helper functions
  const startOfDay = (d: Date) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };
  const endOfDay = (d: Date) => {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x;
  };
  const startOfWeekMonday = (d: Date) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    const day = x.getDay();
    const diffToMonday = (day + 6) % 7;
    x.setDate(x.getDate() - diffToMonday);
    return x;
  };
  const endOfWeekMonday = (d: Date) => {
    const start = startOfWeekMonday(d);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return end;
  };
  const startOfMonth = (d: Date) => {
    const x = new Date(d.getFullYear(), d.getMonth(), 1);
    x.setHours(0, 0, 0, 0);
    return x;
  };
  const endOfMonth = (d: Date) => {
    const x = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    x.setHours(23, 59, 59, 999);
    return x;
  };
  const startOfYear = (d: Date) => {
    const x = new Date(d.getFullYear(), 0, 1);
    x.setHours(0, 0, 0, 0);
    return x;
  };
  const endOfYear = (d: Date) => {
    const x = new Date(d.getFullYear(), 11, 31);
    x.setHours(23, 59, 59, 999);
    return x;
  };

  const getRangeForPreset = (preset: string): { start: Date | null; end: Date | null } => {
    const now = new Date();
    switch (preset) {
      case 'Last hour': {
        // Dernière heure (affichage en minutes)
        const start = new Date(now.getTime() - 60 * 60 * 1000);
        return { start, end: now };
      }
      case 'Last 24 hours': {
        // Dernières 24 heures (affichage toutes les 30 minutes)
        const start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        return { start, end: now };
      }
      case 'Last 7 days': {
        // 7 derniers jours (affichage toutes les 4 heures)
        const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        return { start, end: now };
      }
      case 'Last 30 days': {
        // 30 derniers jours (affichage journalier)
        const start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        return { start, end: now };
      }
      case 'Last 365 days': {
        // 365 derniers jours (affichage journalier adapté)
        const start = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
        return { start, end: now };
      }
      case 'All time': {
        return { start: null, end: now };
      }
      default: {
        return { start: selectedStartDate, end: selectedEndDate };
      }
    }
  };

  const formatDate = (d: Date) => {
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${month}/${day}/${d.getFullYear()}`;
  };

  const getSelectedRangeLabel = (): string => {
    if (selectedPreset && selectedPreset !== 'Custom range') {
      return selectedPreset;
    }
    if (selectedStartDate && selectedEndDate) {
      const sameDay = selectedStartDate.toDateString() === selectedEndDate.toDateString();
      if (sameDay) return formatDate(selectedStartDate);
      return `${formatDate(selectedStartDate)} - ${formatDate(selectedEndDate)}`;
    }
    return '';
  };

  // Helper function to generate default zero data when no real data is available
  const getDefaultZeroData = (): Array<{ date: string; value: number }> => {
    const data: Array<{ date: string; value: number }> = [];
    const now = new Date();
    const start = selectedStartDate || new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    
    // Generate 10 points spread across the period
    const periodMs = now.getTime() - start.getTime();
    const intervalMs = periodMs / 9; // 10 points = 9 intervals
    
    for (let i = 0; i < 10; i++) {
      const pointDate = new Date(start.getTime() + (intervalMs * i));
      data.push({
        date: pointDate.toISOString(),
        value: 0
      });
    }
    
    return data;
  };

  // Helper function to fill missing intervals (minutes, hours, or days) with zero values
  const fillMissingIntervals = (
    apiData: Array<{ timestamp: number; [key: string]: any }>,
    startDate: Date,
    endDate: Date,
    valueExtractor: (item: any) => number
  ): Array<{ date: string; value: number }> => {
    // Determine granularity based on time range
    const diffMs = endDate.getTime() - startDate.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);
    
    let intervalMs: number;
    let keyFormat: (date: Date) => string;
    
    if (diffHours <= 1) {
      // Last hour: interval = 1 minute
      intervalMs = 60 * 1000; // 1 minute
      keyFormat = (date: Date) => {
        const d = new Date(date);
        d.setSeconds(0, 0);
        return d.toISOString(); // Full timestamp rounded to minute
      };
    } else if (diffHours <= 24) {
      // Last 24 hours: interval = 1 hour
      intervalMs = 60 * 60 * 1000; // 1 hour
      keyFormat = (date: Date) => {
        const d = new Date(date);
        d.setMinutes(0, 0, 0);
        return d.toISOString(); // Full timestamp rounded to hour
      };
    } else {
      // More than 24 hours: interval = 1 day
      intervalMs = 24 * 60 * 60 * 1000; // 1 day
      keyFormat = (date: Date) => date.toISOString().split('T')[0]; // YYYY-MM-DD
    }

    // Create a map of existing data keyed by formatted date
    const dataMap = new Map<string, number>();
    apiData.forEach((item) => {
      const date = new Date(item.timestamp * 1000);
      const dateKey = keyFormat(date);
      // If multiple values for same key, sum them
      const existing = dataMap.get(dateKey) || 0;
      dataMap.set(dateKey, existing + valueExtractor(item));
    });

    // Generate array with all intervals between start and end
    const result: Array<{ date: string; value: number }> = [];
    const currentDate = new Date(startDate);
    
    // Round start date to appropriate interval
    if (diffHours <= 1) {
      currentDate.setSeconds(0, 0);
    } else if (diffHours <= 24) {
      currentDate.setMinutes(0, 0, 0);
    } else {
      currentDate.setHours(0, 0, 0, 0);
    }

    while (currentDate <= endDate) {
      const dateKey = keyFormat(currentDate);
      const value = dataMap.get(dateKey) || 0; // Use 0 if no data for this interval
      
      result.push({
        date: currentDate.toISOString(),
        value: value
      });
      
      // Move to next interval
      currentDate.setTime(currentDate.getTime() + intervalMs);
    }

    return result;
  };

  // Helper to get appropriate date format for charts based on time range
  const getChartDateFormat = (date: Date): { short: string; full: string } => {
    if (!selectedStartDate || !selectedEndDate) {
      return {
        short: format(date, 'MM/dd'),
        full: format(date, 'MM/dd/yyyy')
      };
    }

    const diffMs = selectedEndDate.getTime() - selectedStartDate.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);

    if (diffHours <= 1) {
      // Last hour: show time in minutes
      return {
        short: format(date, 'HH:mm'),
        full: format(date, 'MM/dd/yyyy HH:mm')
      };
    } else if (diffHours <= 24) {
      // Last 24 hours: show time in hours
      return {
        short: format(date, 'HH:mm'),
        full: format(date, 'MM/dd/yyyy HH:mm')
      };
    } else {
      // More than 24 hours: show date
      return {
        short: format(date, 'MM/dd'),
        full: format(date, 'MM/dd/yyyy')
      };
    }
  };

  // Helper to generate 10 evenly distributed tick indices for X-axis
  const getXAxisTickIndices = (dataLength: number): number[] => {
    if (dataLength <= 10) {
      return Array.from({ length: dataLength }, (_, i) => i);
    }
    const indices: number[] = [];
    const step = (dataLength - 1) / 9; // 10 ticks = 9 intervals
    for (let i = 0; i < 10; i++) {
      indices.push(Math.round(i * step));
    }
    return indices;
  };

  const openDatePicker = (e: React.MouseEvent<HTMLElement>) => {
    setDateAnchorEl(e.currentTarget);
    setDatePickerOpen(true);
  };
  const closeDatePicker = () => setDatePickerOpen(false);

  // Note: Fonctions de confirmation d'édition et de suppression retirées car les boutons ont été supprimés

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full overflow-hidden">
        <Typography>Loading storage unit details...</Typography>
      </div>
    );
  }

  if (!unit) {
    return (
      <div className="flex justify-center items-center h-full overflow-hidden">
        <Typography>Storage unit not found</Typography>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto" style={{ backgroundColor: '#FFFFFF', paddingLeft: '40px', paddingRight: '40px', paddingTop: '24px', paddingBottom: '24px' }}>
      {/* Page Header - aligned like StorageUnits */}
      <div style={{ marginBottom: '24px' }}>
        <Typography
          style={{
            fontSize: '32px',
            fontWeight: 700,
            color: '#1F2937',
            marginBottom: '8px',
            fontFamily: 'Inter',
            letterSpacing: '-0.02em',
          }}
        >
          Cloud Storage
        </Typography>
        <Typography
          style={{
            fontSize: '16px',
            fontWeight: 400,
            color: '#6B7280',
            fontFamily: 'Inter',
            lineHeight: '24px',
          }}
        >
          Manage your cloud storage buckets and their configurations.
        </Typography>
      </div>

      {/* Details Sub-section */}
      <div style={{ marginBottom: '24px' }}>
        <Button
          variant="text"
          startIcon={<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>}
          onClick={() => navigate('/cloud-storage/buckets')}
          sx={{ mb: 2, color: '#617BFF', textTransform: 'none', fontWeight: 500, border: '1px solid #617BFF', borderRadius: '8px', px: 2, '&:hover': { bgcolor: '#F5F7FF' } }}
        >
          Back to Buckets
        </Button>
        <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#101828', mb: 0.5 }}>
          Details
        </Typography>
        <Typography sx={{ fontSize: '14px', color: '#667085' }}>
          View and manage your bucket configuration and metrics.
        </Typography>
      </div>
      <div className="max-w-7xl mx-auto">
        <div className="space-y-6">
          {/* Header card: 1080x85, only bottom border, centered */}
          <div className="flex justify-center">
            <div className="w-[1080px] h-[85px] flex items-center justify-between px-4 border-b border-[#E9EAEB] mb-6">
              <div className="flex flex-col items-start">
                <div className="flex items-center gap-3">
                  {/* Provider Logo */}
                  <div
                    className="flex items-center justify-center"
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '200px',
                      backgroundColor: '#FFFFFF',
                      border: '0.5px solid #E5E7EB',
                    }}
                  >
                    {unit?.storageType === ApiTypes.StorageType.S3 && (
                      <img src={logoAws} alt="AWS S3" style={{ width: '18px', height: '18px' }} />
                    )}
                    {unit?.storageType === ApiTypes.StorageType.GCS && (
                      <img src={logoGoogle} alt="Google Cloud Storage" style={{ width: '18px', height: '18px' }} />
                    )}
                    {unit?.storageType === ApiTypes.StorageType.BLOB && (
                      <img src={logoAzure} alt="Azure Blob Storage" style={{ width: '18px', height: '18px' }} />
                    )}
                  </div>
                  <Typography
                    sx={{ color: '#101828', fontWeight: 600, fontSize: '20px', lineHeight: '30px' }}
                  >
                    {unit?.name}
                  </Typography>
                </div>
                {/* Buttons row under title */}
                <div className="mt-4 flex items-center space-x-2">
                  {/* Short ID button (first 5 chars) - copies full ID */}
                  <button
                    title="Copy unit ID"
                    onClick={() => unit?.id && handleCopy(unit.id)}
                    style={{
                      height: 22,
                      background: '#FFFFFF',
                      borderRadius: 6,
                      border: '1px solid #D5D7DA',
                      padding: '0 8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#414651',
                      fontWeight: 500,
                      fontSize: 12,
                      lineHeight: '18px',
                      textAlign: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    {unit?.id ? unit.id.slice(0, 5) : ''}
                  </button>

                  {/* Storage type button */}
                  <button
                    style={{
                      height: 22,
                      background: '#FFFFFF',
                      borderRadius: 6,
                      border: '1px solid #D5D7DA',
                      padding: '0 8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#414651',
                      fontWeight: 500,
                      fontSize: 12,
                      lineHeight: '18px',
                      textTransform: 'uppercase',
                      textAlign: 'center',
                    }}
                  >
                    {storageTypeLabel.toUpperCase()}
                  </button>

                  {/* Status button (online/offline) - based on node stats */}
                  <button
                    style={{
                      height: 22,
                      background: '#FFFFFF',
                      borderRadius: 6,
                      border: '1px solid #D5D7DA',
                      padding: '0 8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#414651',
                      fontWeight: 500,
                      fontSize: 12,
                      lineHeight: '18px',
                      // no text transform to preserve capitalization
                      textAlign: 'center',
                    }}
                  >
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background:
                          nodeStatsMinute.some((node) => node.nodeStatus === 'ONLINE')
                            ? '#17B26A'
                            : '#F04438',
                        display: 'inline-block',
                        marginRight: 6,
                      }}
                    />
                    {nodeStatsMinute.some((node) => node.nodeStatus === 'ONLINE')
                      ? 'Online'
                      : 'Offline'}
                  </button>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                {/* Reload button (style aligned with StorageRepoDetails) */}
                <button
                  onClick={() => {
                    window.location.reload();
                  }}
                  className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    borderWidth: '1px',
                    padding: '10px',
                    border: '1px solid #D1D5DB',
                  }}
                  title="Reload data"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </button>

                {/* Settings gear icon - opens edit popup directly */}
                <button
                  onClick={() => {
                    if (unit) {
                      setNewUnit({
                        ...unit,
                        name: unit.name,
                        storageType: unit.storageType,
                        apiKeyId: (unit as any).apiKeyId || (unit as any).apiKey?.id,
                      });
                      if (unit.storageType === ApiTypes.StorageType.S3) {
                        setS3Fields({
                          accessKey: unit.key || '',
                          secret: typeof unit.secret === 'string' ? unit.secret : '',
                          region: unit.region || '',
                          bucket: unit.bucket || '',
                          endpoint: unit.endpoint || '',
                        });
                      } else if (unit.storageType === ApiTypes.StorageType.GCS) {
                        setGcsFields({
                          clientEmail: unit.key || '',
                          privateKey: typeof unit.secret === 'string' ? unit.secret : '',
                          bucket: unit.bucket || '',
                          endpoint: unit.endpoint || '',
                        });
                      } else if (unit.storageType === ApiTypes.StorageType.BLOB) {
                        setAzureFields({
                          storageAccount: unit.key || '',
                          container: unit.bucket || '',
                          accessKey: unit.key || '',
                        });
                      }
                      setOpenEditUnit(true);
                    }
                  }}
                  title="Settings"
                  className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    padding: '10px',
                    border: '1px solid #D1D5DB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3"/>
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                  </svg>
                </button>

                {/* Delete button */}
                <button
                  onClick={() => setDeleteDialogOpen(true)}
                  title="Delete"
                  className="transition-colors"
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    padding: '10px',
                    border: '1px solid #FEE2E2',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#EF4444',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#FEE2E2';
                    e.currentTarget.style.borderColor = '#EF4444';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.borderColor = '#FEE2E2';
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    <line x1="10" y1="11" x2="10" y2="17"></line>
                    <line x1="14" y1="11" x2="14" y2="17"></line>
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Removed duplicate header with purple left bar to align with first card */}

          {/* Statistics Cards Section (title removed) */}
          <div className="flex flex-col mt-10 md:mt-14 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-2 justify-items-center">
              {/* Number of calls card */}
              <div className="relative" style={{ width: '528px', height: '372px' }}>
                {/* Outer card - light gray background */}
                <div
                  className="absolute w-full h-full rounded-xl"
                  style={{
                    backgroundColor: '#FDFDFD',
                    borderRadius: '12px',
                    border: '1px solid #E9EAEB',
                  }}
                >
                  {/* Title area */}
                  <div className="pt-3 px-4 flex items-center justify-between">
                    <Typography
                      style={{
                        color: '#181D27',
                        fontWeight: 600,
                        fontSize: '14px',
                        lineHeight: '20px',
                      }}
                    >
                      Requests (30d)
                    </Typography>
                    <Typography
                      style={{
                        color: '#535862',
                        fontWeight: 500,
                        fontSize: '13px',
                        lineHeight: '20px',
                      }}
                    >
                      {stats.calls.value === 0 ? '-' : formatNumber(stats.calls.value)}
                    </Typography>
                  </div>
                </div>

                {/* Inner white card - contains data and chart */}
                <div
                  className="absolute bg-white rounded-xl shadow-sm"
                  style={{
                    width: '528px',
                    height: '330px',
                    top: '42px',
                    borderRadius: '12px',
                    border: '1px solid #E9EAEB',
                  }}
                >
                  <div className="p-4 h-full flex flex-col">
                    <div className="flex-1">
                      {/* Chart with data from API - line chart using Recharts */}
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                          data={(stats.calls.history.length > 0 ? stats.calls.history : getDefaultZeroData()).map((point) => {
                            const dateFormats = getChartDateFormat(new Date(point.date));
                            return {
                              name: dateFormats.short,
                              value: point.value,
                              fullDate: dateFormats.full,
                            };
                          })}
                          margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                        >
                          <defs>
                            <linearGradient id="colorCalls" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#788CFF" stopOpacity={0.8} />
                              <stop offset="95%" stopColor="#788CFF" stopOpacity={0.1} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={true}
                            stroke="#E5E7EB"
                          />
                          <XAxis
                            dataKey="name"
                            tick={{ fontSize: 11, fill: '#6B7280' }}
                            axisLine={{ stroke: '#E5E7EB' }}
                            tickLine={false}
                            ticks={(() => {
                              const data = (stats.calls.history.length > 0 ? stats.calls.history : getDefaultZeroData());
                              const indices = getXAxisTickIndices(data.length);
                              return indices.map(i => getChartDateFormat(new Date(data[i]?.date || new Date())).short);
                            })()}
                            interval={0}
                          />
                          <YAxis
                            domain={[0, (dataMax: number) => roundToNiceDomain(Math.max(dataMax, 1000))]}
                            ticks={(() => {
                              const callsData = stats.calls.history.length > 0 ? stats.calls.history : getDefaultZeroData();
                              const dataMax = Math.max(...callsData.map(d => d.value), 1000);
                              const max = roundToNiceDomain(dataMax);
                              const ticks: number[] = [];
                              const step = max / 5;
                              for (let i = 0; i <= 5; i++) {
                                ticks.push(step * i);
                              }
                              return ticks;
                            })()}
                            tick={{ fontSize: 11, fill: '#6B7280' }}
                            tickLine={false}
                            axisLine={{ stroke: '#E5E7EB' }}
                            tickFormatter={(value: number) => value === 0 ? '' : formatNumber(value)}
                          />
                          <RechartsTooltip
                            formatter={(value) => [`${value}`, 'Requests']}
                            labelFormatter={(label: any, payload: any) => {
                              if (payload && payload.length > 0 && payload[0]?.payload) {
                                return payload[0].payload.fullDate || label;
                              }
                              return label;
                            }}
                            contentStyle={{ fontSize: '12px', borderRadius: '4px' }}
                          />
                          <Area
                            type="monotone"
                            dataKey="value"
                            stroke="#788CFF"
                            fillOpacity={1}
                            fill="url(#colorCalls)"
                            strokeWidth={2}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>

              {/* Repositories linked card - moved here from bottom */}
              <div className="relative" style={{ width: '528px', height: '372px' }}>
                {/* Outer gray card */}
                <div
                  className="absolute w-full h-full rounded-xl"
                  style={{
                    backgroundColor: '#FDFDFD',
                    borderRadius: '12px',
                    border: '1px solid #E9EAEB',
                  }}
                >
                  {/* Title area */}
                  <div className="pt-3 px-4 flex items-center justify-between">
                    <Typography
                      style={{
                        color: '#181D27',
                        fontWeight: 600,
                        fontSize: '14px',
                        lineHeight: '20px',
                      }}
                    >
                      Repositories linked
                    </Typography>
                    <Typography
                      style={{
                        color: '#535862',
                        fontWeight: 500,
                        fontSize: '13px',
                        lineHeight: '20px',
                      }}
                    >
                      {associatedRepos.length}
                    </Typography>
                  </div>
                </div>

                {/* Inner white card */}
                <div
                  className="absolute bg-white rounded-xl shadow-sm"
                  style={{
                    width: '528px',
                    height: '330px',
                    top: '42px',
                    borderRadius: '12px',
                    border: '1px solid #E9EAEB',
                  }}
                >
                  {/* Table inside a third inner card (488x290) */}
                  <div className="h-full w-full flex items-center justify-center">
                    <div
                      className="bg-white rounded-xl"
                      style={{ width: '488px', height: '290px', border: '1px solid #E9EAEB' }}
                    >
                      <div className="h-full flex flex-col">
                        {/* Header */}
                        <div className="grid grid-cols-2 text-xs text-gray-500 px-4 py-2 border-b border-gray-100 rounded-t-xl">
                          <div>Name</div>
                          <div>Mode</div>
                        </div>
                        {/* Rows */}
                        <div className="flex-1 overflow-auto">
                          {loadingRepos ? (
                            <div className="h-full flex items-center justify-center text-gray-500 text-sm">
                              Loading associated repos...
                            </div>
                          ) : associatedRepos.length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center text-gray-500 text-sm p-4">
                              <div className="mb-2">No associated repos found</div>
                              <div className="text-xs text-gray-400">
                                Check console for debugging info
                              </div>
                            </div>
                          ) : (
                            <>
                              {associatedRepos.map((repo) => (
                                <div
                                  key={repo.id}
                                  className="grid grid-cols-2 items-center px-4 py-3 border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors"
                                  onClick={() => {
                                    navigate(`/repositories/inventory/${repo.id}`);
                                  }}
                                  title={`View details for ${repo.name}`}
                                >
                                  <div className="text-gray-900 font-medium truncate">
                                    {repo.name}
                                  </div>
                                  <div className="text-gray-600 text-sm truncate">
                                    {String(repo.storageType || (repo as any).mode || 'N/A')}
                                  </div>
                                </div>
                              ))}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Upload/Download merged card - Single graph with two curves */}
              <div className="relative" style={{ width: '528px', height: '372px' }}>
                {/* Outer card - light gray background */}
                <div
                  className="absolute w-full h-full rounded-xl"
                  style={{
                    backgroundColor: '#FDFDFD',
                    borderRadius: '12px',
                    border: '1px solid #E9EAEB',
                  }}
                >
                  {/* Title area */}
                  <div className="pt-3 px-4 flex items-center justify-between">
                    <Typography
                      style={{
                        color: '#181D27',
                        fontWeight: 600,
                        fontSize: '14px',
                        lineHeight: '20px',
                      }}
                    >
                      Traffic (30d)
                    </Typography>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1">
                        <ArrowUpwardIcon sx={{ fontSize: 14, color: '#10B981' }} />
                        <span style={{ fontSize: '13px', fontWeight: 500, color: '#535862' }}>
                          {stats.upload.value === 0 
                            ? '-' 
                            : stats.upload.value >= 1 
                              ? `${stats.upload.value.toFixed(2)} GB` 
                              : `${(stats.upload.value * 1024).toFixed(0)} MB`}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <ArrowDownwardIcon sx={{ fontSize: 14, color: '#3B82F6' }} />
                        <span style={{ fontSize: '13px', fontWeight: 500, color: '#535862' }}>
                          {stats.download.value === 0 
                            ? '-' 
                            : stats.download.value >= 1 
                              ? `${stats.download.value.toFixed(2)} GB` 
                              : `${(stats.download.value * 1024).toFixed(0)} MB`}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Inner white card - contains single combined chart */}
                <div
                  className="absolute bg-white rounded-xl shadow-sm"
                  style={{
                    width: '528px',
                    height: '330px',
                    top: '42px',
                    borderRadius: '12px',
                    border: '1px solid #E9EAEB',
                  }}
                >
                  <div className="p-4 h-full flex flex-col">

                    {/* Combined Chart */}
                    <div className="flex-1">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                          data={(stats.upload.history.length > 0 ? stats.upload.history : getDefaultZeroData()).map((point, index) => {
                            const dateFormats = getChartDateFormat(new Date(point.date));
                            return {
                              name: dateFormats.short,
                              upload: point.value,
                              download: (stats.download.history.length > 0 ? stats.download.history : getDefaultZeroData())[index]?.value || 0,
                              fullDate: dateFormats.full,
                            };
                          })}
                          margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                        >
                          <defs>
                            <linearGradient id="colorUpload" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="colorDownload" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={true}
                            stroke="#E5E7EB"
                          />
                          <XAxis
                            dataKey="name"
                            tick={{ fontSize: 11, fill: '#6B7280' }}
                            tickLine={false}
                            axisLine={{ stroke: '#E5E7EB' }}
                            ticks={(() => {
                              const data = (stats.upload.history.length > 0 ? stats.upload.history : getDefaultZeroData());
                              const indices = getXAxisTickIndices(data.length);
                              return indices.map(i => getChartDateFormat(new Date(data[i]?.date || new Date())).short);
                            })()}
                            interval={0}
                          />
                          <YAxis
                            domain={[0, (dataMax: number) => roundToNiceDomain(dataMax)]}
                            ticks={(() => {
                              // Calculer les ticks explicitement pour garantir 5 paliers égaux
                              const uploadData = stats.upload.history.length > 0 ? stats.upload.history : getDefaultZeroData();
                              const downloadData = stats.download.history.length > 0 ? stats.download.history : getDefaultZeroData();
                              const allValues = [...uploadData.map(d => d.value), ...downloadData.map(d => d.value)];
                              const dataMax = Math.max(...allValues, 0);
                              const max = roundToNiceDomain(dataMax);
                              
                              // Générer 6 ticks (0 + 5 sections)
                              const ticks: number[] = [];
                              const step = max / 5;
                              for (let i = 0; i <= 5; i++) {
                                ticks.push(step * i);
                              }
                              return ticks;
                            })()}
                            tick={{ fontSize: 11, fill: '#6B7280' }}
                            tickLine={false}
                            axisLine={{ stroke: '#E5E7EB' }}
                            tickFormatter={(value: number) => {
                              if (value >= 1) return `${value.toFixed(1)} GB`;
                              if (value >= 0.01) return `${value.toFixed(2)} GB`;
                              return `${(value * 1024).toFixed(0)} MB`;
                            }}
                          />
                          <RechartsTooltip
                            formatter={(value: number) => {
                              if (value >= 1) return [`${value.toFixed(2)} GB`, ''];
                              if (value >= 0.01) return [`${value.toFixed(3)} GB`, ''];
                              return [`${(value * 1024).toFixed(1)} MB`, ''];
                            }}
                            labelFormatter={(label: any, payload: any) => {
                              if (payload && payload.length > 0 && payload[0]?.payload) {
                                return payload[0].payload.fullDate || label;
                              }
                              return label;
                            }}
                            labelStyle={{ color: '#111827', fontWeight: 600 }}
                            contentStyle={{
                              borderRadius: '8px',
                              border: '1px solid #E5E7EB',
                              backgroundColor: 'white',
                              padding: '8px 12px',
                            }}
                          />
                          <Area
                            type="monotone"
                            dataKey="upload"
                            stroke="#10B981"
                            fill="url(#colorUpload)"
                            strokeWidth={2}
                            name="Upload"
                          />
                          <Area
                            type="monotone"
                            dataKey="download"
                            stroke="#3B82F6"
                            fill="url(#colorDownload)"
                            strokeWidth={2}
                            name="Download"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>
              {/* Storage change card */}
              <div className="relative" style={{ width: '528px', height: '372px' }}>
                {/* Outer card - light gray background */}
                <div
                  className="absolute w-full h-full rounded-xl"
                  style={{
                    backgroundColor: '#FDFDFD',
                    borderRadius: '12px',
                    border: '1px solid #E9EAEB',
                  }}
                >
                  {/* Title area */}
                  <div className="pt-3 px-4 flex items-center justify-between">
                    <Typography
                      style={{
                        color: '#181D27',
                        fontWeight: 600,
                        fontSize: '14px',
                        lineHeight: '20px',
                      }}
                    >
                      Storage Change (30d)
                    </Typography>
                    <div className="flex items-center gap-2">
                      <Typography
                        style={{
                          color: '#535862',
                          fontWeight: 500,
                          fontSize: '13px',
                          lineHeight: '20px',
                        }}
                      >
                        {stats.storageChange.value === 0 ? '-' : `${parseFloat((stats.storageChange.value / (1024 * 1024 * 1024)).toFixed(2))} GB`}
                      </Typography>
                      {stats.storageChange.value !== 0 && (
                        <div
                          className={`flex items-center text-xs ${
                            stats.storageChange.change >= 0 ? 'text-green-500' : 'text-red-500'
                          }`}
                        >
                          {stats.storageChange.change >= 0 ? (
                            <TrendingUpIcon sx={{ fontSize: 14 }} />
                          ) : (
                            <TrendingDownIcon sx={{ fontSize: 14 }} />
                          )}
                          <span style={{ fontSize: '13px', fontWeight: 500 }}>
                            {Math.abs(stats.storageChange.change).toFixed(1)}%
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Inner white card - contains data and chart */}
                <div
                  className="absolute bg-white rounded-xl shadow-sm"
                  style={{
                    width: '528px',
                    height: '330px',
                    top: '42px',
                    borderRadius: '12px',
                    border: '1px solid #E9EAEB',
                  }}
                >
                  <div className="p-4">
                    <div className="h-72">
                      {/* Chart with data from API using Recharts */}
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={(stats.storageChange.history.length > 0 ? stats.storageChange.history : getDefaultZeroData()).map((point) => {
                            const dateFormats = getChartDateFormat(new Date(point.date));
                            return {
                              name: dateFormats.short,
                              value: point.value,
                              fullDate: dateFormats.full,
                            };
                          })}
                          margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={true}
                            stroke="#E5E7EB"
                          />
                          <XAxis
                            dataKey="name"
                            tick={{ fontSize: 11, fill: '#6B7280' }}
                            axisLine={{ stroke: '#E5E7EB' }}
                            tickLine={false}
                            ticks={(() => {
                              const data = (stats.storageChange.history.length > 0 ? stats.storageChange.history : getDefaultZeroData());
                              const indices = getXAxisTickIndices(data.length);
                              return indices.map(i => getChartDateFormat(new Date(data[i]?.date || new Date())).short);
                            })()}
                            interval={0}
                          />
                          <YAxis 
                            domain={(() => {
                              // Symmetric Y-axis in GB
                              const data = stats.storageChange.history.length > 0 ? stats.storageChange.history : getDefaultZeroData();
                              const values = data.map(d => d.value);
                              const maxAbs = Math.max(Math.abs(Math.min(...values, 0)), Math.abs(Math.max(...values, 0)));
                              const roundedMax = maxAbs === 0 ? 1 : roundToNiceDomain(maxAbs); // 1 GB default
                              return [-roundedMax, roundedMax];
                            })()}
                            ticks={(() => {
                              // Symmetric ticks in GB: +5 / -5 segments around 0
                              const data = stats.storageChange.history.length > 0 ? stats.storageChange.history : getDefaultZeroData();
                              const values = data.map(d => d.value);
                              const maxAbs = Math.max(Math.abs(Math.min(...values, 0)), Math.abs(Math.max(...values, 0)));
                              const roundedMax = maxAbs === 0 ? 1 : roundToNiceDomain(maxAbs);
                              
                              const ticks: number[] = [];
                              const step = roundedMax / 5;
                              for (let i = -5; i <= 5; i++) {
                                ticks.push(step * i);
                              }
                              return ticks;
                            })()}
                            tick={{ fontSize: 11, fill: '#6B7280' }}
                            axisLine={{ stroke: '#E5E7EB' }}
                            tickLine={false}
                            tickFormatter={(value: number) => value === 0 ? '0' : `${parseFloat(value.toFixed(2))} GB`}
                            tickCount={11}
                          />
                          <RechartsTooltip
                            formatter={(value: number) => [`${parseFloat(value.toFixed(3))} GB`, 'Change']}
                            labelFormatter={(label: any, payload: any) => {
                              if (payload && payload.length > 0 && payload[0]?.payload) {
                                return payload[0].payload.fullDate || label;
                              }
                              return label;
                            }}
                            contentStyle={{ fontSize: '12px', borderRadius: '4px' }}
                          />
                          {/* Reference line at zero to separate positive and negative */}
                          <ReferenceLine y={0} stroke="#9CA3AF" strokeWidth={1} strokeDasharray="3 3" />
                          <Bar
                            dataKey="value"
                            fill="#10b981" /* Default fill color, will be overridden by Cell */
                            barSize={8}
                            radius={[4, 4, 4, 4]}
                          >
                            {/* Use Cell components to color bars based on value */}
                            {(stats.storageChange.history.length > 0 ? stats.storageChange.history : getDefaultZeroData()).map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={entry.value >= 0 ? '#10b981' : '#ef4444'}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Unit Details Section - Repos card moved to top grid */}

          {/* AI Recommendations Section */}
          <div className="flex flex-col">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-2 justify-items-center">
              {/* Right: Unit Details as double card with General Information */}
              {/* AI RECOMMENDATIONS SECTION - COMMENTED OUT */}
              {/* eslint-disable-next-line no-constant-condition */}
              {false ? (
                <div>
                  <div className="relative" style={{ width: '528px', height: '314px' }}>
                    {/* Outer gray card */}
                    <div
                      className="absolute w-full h-full rounded-xl"
                      style={{
                        backgroundColor: '#FDFDFD',
                        borderRadius: '12px',
                        border: '1px solid #E9EAEB',
                      }}
                    >
                      {/* Title area */}
                      <div className="pt-3 px-4">
                        <Typography
                          style={{
                            color: '#181D27',
                            fontWeight: 600,
                            fontSize: '14px',
                            lineHeight: '20px',
                          }}
                        >
                          AI Recommendations
                        </Typography>
                      </div>
                    </div>

                    {/* Inner white card */}
                    <div
                      className="absolute bg-white rounded-xl shadow-sm"
                      style={{
                        width: '528px',
                        height: '272px',
                        top: '42px',
                        borderRadius: '12px',
                        border: '1px solid #E9EAEB',
                      }}
                    >
                      <div
                        className="w-full bg-white border border-gray-200 shadow-sm flex flex-col"
                        style={{
                          height: '100%',
                          borderRadius: '12px',
                          padding: '16px',
                          position: 'relative',
                          zIndex: 2,
                        }}
                      >
                        <div
                          style={{
                            maxHeight: 240,
                            overflowX: 'auto',
                            overflowY: 'hidden',
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                            WebkitOverflowScrolling: 'touch',
                          }}
                        >
                          <style>{`
                  div::-webkit-scrollbar {
                    display: none;
                  }
                `}</style>
                          <div className="flex space-x-4" style={{ paddingBottom: '4px' }}>
                            {/* Card 1 */}
                            <div
                              style={{
                                width: 216,
                                height: 235,
                                minWidth: '216px',
                                borderRadius: '12px',
                                borderWidth: '1px',
                                borderColor: '#e5e7eb',
                                borderStyle: 'solid',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                padding: '20px',
                                backgroundColor: 'white',
                              }}
                            >
                              <div>
                                <Typography
                                  style={{
                                    fontWeight: 600,
                                    fontSize: '18px',
                                    color: '#181D27',
                                    lineHeight: '26px',
                                    marginBottom: '8px',
                                  }}
                                >
                                  Export log activity to spreadsheet
                                </Typography>
                                <div className="flex gap-2 mb-2">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    style={{
                                      width: 43,
                                      height: 24,
                                      borderRadius: '6px',
                                      borderWidth: '1px',
                                      borderColor: '#D5D7DA',
                                      color: '#414651',
                                      gap: '3px',
                                      paddingTop: '3px',
                                      paddingRight: '8px',
                                      paddingBottom: '3px',
                                      paddingLeft: '8px',
                                      minWidth: 'auto',
                                      fontSize: '12px',
                                      textTransform: 'none',
                                    }}
                                  >
                                    data
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    style={{
                                      width: 43,
                                      height: 24,
                                      borderRadius: '6px',
                                      borderWidth: '1px',
                                      borderColor: '#D5D7DA',
                                      color: '#414651',
                                      gap: '3px',
                                      paddingTop: '3px',
                                      paddingRight: '8px',
                                      paddingBottom: '3px',
                                      paddingLeft: '8px',
                                      minWidth: 'auto',
                                      fontSize: '12px',
                                      textTransform: 'none',
                                    }}
                                  >
                                    excel
                                  </Button>
                                </div>
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  marginTop: '12px',
                                }}
                              >
                                <div
                                  style={{
                                    color: '#4766FF',
                                    fontWeight: 600,
                                    fontSize: '16px',
                                    lineHeight: '24px',
                                    cursor: 'pointer',
                                  }}
                                >
                                  Enable skill
                                </div>
                                <div
                                  style={{
                                    width: '16px',
                                    height: '16px',
                                    borderRadius: '50%',
                                    border: '1px solid #A4A7AE',
                                    color: '#A4A7AE',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    fontWeight: 500,
                                    fontSize: '10px',
                                    cursor: 'pointer',
                                    lineHeight: '10px',
                                  }}
                                >
                                  i
                                </div>
                              </div>
                            </div>

                            {/* Card 2 */}
                            <div
                              style={{
                                width: 216,
                                height: 235,
                                minWidth: '216px',
                                borderRadius: '12px',
                                borderWidth: '1px',
                                borderColor: '#e5e7eb',
                                borderStyle: 'solid',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                padding: '20px',
                                backgroundColor: 'white',
                              }}
                            >
                              <div>
                                <Typography
                                  style={{
                                    fontWeight: 600,
                                    fontSize: '18px',
                                    color: '#181D27',
                                    lineHeight: '26px',
                                    marginBottom: '8px',
                                  }}
                                >
                                  Export log activity to spreadsheet
                                </Typography>
                                <div className="flex gap-2 mb-2">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    style={{
                                      width: 43,
                                      height: 24,
                                      borderRadius: '6px',
                                      borderWidth: '1px',
                                      borderColor: '#D5D7DA',
                                      color: '#414651',
                                      gap: '3px',
                                      paddingTop: '3px',
                                      paddingRight: '8px',
                                      paddingBottom: '3px',
                                      paddingLeft: '8px',
                                      minWidth: 'auto',
                                      fontSize: '12px',
                                      textTransform: 'none',
                                    }}
                                  >
                                    data
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    style={{
                                      width: 43,
                                      height: 24,
                                      borderRadius: '6px',
                                      borderWidth: '1px',
                                      borderColor: '#D5D7DA',
                                      color: '#414651',
                                      gap: '3px',
                                      paddingTop: '3px',
                                      paddingRight: '8px',
                                      paddingBottom: '3px',
                                      paddingLeft: '8px',
                                      minWidth: 'auto',
                                      fontSize: '12px',
                                      textTransform: 'none',
                                    }}
                                  >
                                    excel
                                  </Button>
                                </div>
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  marginTop: '12px',
                                }}
                              >
                                <div
                                  style={{
                                    color: '#4766FF',
                                    fontWeight: 600,
                                    fontSize: '16px',
                                    lineHeight: '24px',
                                    cursor: 'pointer',
                                  }}
                                >
                                  Enable skill
                                </div>
                                <div
                                  style={{
                                    width: '16px',
                                    height: '16px',
                                    borderRadius: '50%',
                                    border: '1px solid #A4A7AE',
                                    color: '#A4A7AE',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    fontWeight: 500,
                                    fontSize: '10px',
                                    cursor: 'pointer',
                                    lineHeight: '10px',
                                  }}
                                >
                                  i
                                </div>
                              </div>
                            </div>

                            {/* Card 3 */}
                            <div
                              style={{
                                width: 216,
                                height: 235,
                                minWidth: '216px',
                                borderRadius: '12px',
                                borderWidth: '1px',
                                borderColor: '#e5e7eb',
                                borderStyle: 'solid',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                padding: '20px',
                                backgroundColor: 'white',
                              }}
                            >
                              <div>
                                <Typography
                                  style={{
                                    fontWeight: 600,
                                    fontSize: '18px',
                                    color: '#181D27',
                                    lineHeight: '26px',
                                    marginBottom: '8px',
                                  }}
                                >
                                  Export log activity to spreadsheet
                                </Typography>
                                <div className="flex gap-2 mb-2">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    style={{
                                      width: 43,
                                      height: 24,
                                      borderRadius: '6px',
                                      borderWidth: '1px',
                                      borderColor: '#D5D7DA',
                                      color: '#414651',
                                      gap: '3px',
                                      paddingTop: '3px',
                                      paddingRight: '8px',
                                      paddingBottom: '3px',
                                      paddingLeft: '8px',
                                      minWidth: 'auto',
                                      fontSize: '12px',
                                      textTransform: 'none',
                                    }}
                                  >
                                    data
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    style={{
                                      width: 43,
                                      height: 24,
                                      borderRadius: '6px',
                                      borderWidth: '1px',
                                      borderColor: '#D5D7DA',
                                      color: '#414651',
                                      gap: '3px',
                                      paddingTop: '3px',
                                      paddingRight: '8px',
                                      paddingBottom: '3px',
                                      paddingLeft: '8px',
                                      minWidth: 'auto',
                                      fontSize: '12px',
                                      textTransform: 'none',
                                    }}
                                  >
                                    excel
                                  </Button>
                                </div>
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  marginTop: '12px',
                                }}
                              >
                                <div
                                  style={{
                                    color: '#4766FF',
                                    fontWeight: 600,
                                    fontSize: '16px',
                                    lineHeight: '24px',
                                    cursor: 'pointer',
                                  }}
                                >
                                  Enable skill
                                </div>
                                <div
                                  style={{
                                    width: '16px',
                                    height: '16px',
                                    borderRadius: '50%',
                                    border: '1px solid #A4A7AE',
                                    color: '#A4A7AE',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    fontWeight: 500,
                                    fontSize: '10px',
                                    cursor: 'pointer',
                                    lineHeight: '10px',
                                  }}
                                >
                                  i
                                </div>
                              </div>
                            </div>

                            {/* Card 4 */}
                            <div
                              style={{
                                width: 216,
                                height: 235,
                                minWidth: '216px',
                                borderRadius: '12px',
                                borderWidth: '1px',
                                borderColor: '#e5e7eb',
                                borderStyle: 'solid',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                padding: '20px',
                                backgroundColor: 'white',
                              }}
                            >
                              <div>
                                <Typography
                                  style={{
                                    fontWeight: 600,
                                    fontSize: '18px',
                                    color: '#181D27',
                                    lineHeight: '26px',
                                    marginBottom: '8px',
                                  }}
                                >
                                  Export log activity to spreadsheet
                                </Typography>
                                <div className="flex gap-2 mb-2">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    style={{
                                      width: 43,
                                      height: 24,
                                      borderRadius: '6px',
                                      borderWidth: '1px',
                                      borderColor: '#D5D7DA',
                                      color: '#414651',
                                      gap: '3px',
                                      paddingTop: '3px',
                                      paddingRight: '8px',
                                      paddingBottom: '3px',
                                      paddingLeft: '8px',
                                      minWidth: 'auto',
                                      fontSize: '12px',
                                      textTransform: 'none',
                                    }}
                                  >
                                    data
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    style={{
                                      width: 43,
                                      height: 24,
                                      borderRadius: '6px',
                                      borderWidth: '1px',
                                      borderColor: '#D5D7DA',
                                      color: '#414651',
                                      gap: '3px',
                                      paddingTop: '3px',
                                      paddingRight: '8px',
                                      paddingBottom: '3px',
                                      paddingLeft: '8px',
                                      minWidth: 'auto',
                                      fontSize: '12px',
                                      textTransform: 'none',
                                    }}
                                  >
                                    excel
                                  </Button>
                                </div>
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  marginTop: '12px',
                                }}
                              >
                                <div
                                  style={{
                                    color: '#4766FF',
                                    fontWeight: 600,
                                    fontSize: '16px',
                                    lineHeight: '24px',
                                    cursor: 'pointer',
                                  }}
                                >
                                  Enable skill
                                </div>
                                <div
                                  style={{
                                    width: '16px',
                                    height: '16px',
                                    borderRadius: '50%',
                                    border: '1px solid #A4A7AE',
                                    color: '#A4A7AE',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    fontWeight: 500,
                                    fontSize: '10px',
                                    cursor: 'pointer',
                                    lineHeight: '10px',
                                  }}
                                >
                                  i
                                </div>
                              </div>
                            </div>

                            {/* Card 5 */}
                            <div
                              style={{
                                width: 216,
                                height: 235,
                                minWidth: '216px',
                                borderRadius: '12px',
                                borderWidth: '1px',
                                borderColor: '#e5e7eb',
                                borderStyle: 'solid',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                padding: '20px',
                                backgroundColor: 'white',
                              }}
                            >
                              <div>
                                <Typography
                                  style={{
                                    fontWeight: 600,
                                    fontSize: '18px',
                                    color: '#181D27',
                                    lineHeight: '26px',
                                    marginBottom: '8px',
                                  }}
                                >
                                  Export log activity to spreadsheet
                                </Typography>
                                <div className="flex gap-2 mb-2">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    style={{
                                      width: 43,
                                      height: 24,
                                      borderRadius: '6px',
                                      borderWidth: '1px',
                                      borderColor: '#D5D7DA',
                                      color: '#414651',
                                      gap: '3px',
                                      paddingTop: '3px',
                                      paddingRight: '8px',
                                      paddingBottom: '3px',
                                      paddingLeft: '8px',
                                      minWidth: 'auto',
                                      fontSize: '12px',
                                      textTransform: 'none',
                                    }}
                                  >
                                    data
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    style={{
                                      width: 43,
                                      height: 24,
                                      borderRadius: '6px',
                                      borderWidth: '1px',
                                      borderColor: '#D5D7DA',
                                      color: '#414651',
                                      gap: '3px',
                                      paddingTop: '3px',
                                      paddingRight: '8px',
                                      paddingBottom: '3px',
                                      paddingLeft: '8px',
                                      minWidth: 'auto',
                                      fontSize: '12px',
                                      textTransform: 'none',
                                    }}
                                  >
                                    excel
                                  </Button>
                                </div>
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  marginTop: '12px',
                                }}
                              >
                                <div
                                  style={{
                                    color: '#4766FF',
                                    fontWeight: 600,
                                    fontSize: '16px',
                                    lineHeight: '24px',
                                    cursor: 'pointer',
                                  }}
                                >
                                  Enable skill
                                </div>
                                <div
                                  style={{
                                    width: '16px',
                                    height: '16px',
                                    borderRadius: '50%',
                                    border: '1px solid #A4A7AE',
                                    color: '#A4A7AE',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    fontWeight: 500,
                                    fontSize: '10px',
                                    cursor: 'pointer',
                                    lineHeight: '10px',
                                  }}
                                >
                                  i
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          {/* Nodes Section (title removed) */}
          <div className="flex justify-center mt-8">
            <div
              className="overflow-x-auto overflow-y-auto"
              style={{ 
                width: '1120px', 
                maxHeight: '317px'
              }}
            >
              {loadingNodeStatsMinute ? (
                <div className="flex items-center justify-center" style={{ minHeight: '150px' }}>
                  <CircularProgress size={24} style={{ color: '#788CFF' }} />
                  <Typography className="ml-2">Loading node statistics...</Typography>
                </div>
              ) : nodeStatsMinute.length === 0 ? (
                <div className="flex items-center justify-center" style={{ minHeight: '150px' }}>
                  <Typography>No node statistics available</Typography>
                </div>
              ) : (
                <div>
                  <table className="w-full border border-[#E9EAEB] rounded-xl border-collapse">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Host
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          IP
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Latency
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Version
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Uptime
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Last Updated
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {nodeStatsMinute.map((node) => (
                        <tr key={`${node.nodeId}-${node.unitId}`}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {node.host
                              ? node.host.split('.')[0]
                              : `Node ${node.nodeId.substring(0, 8)}...`}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {node.nodeId}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${(() => {
                                return node.nodeStatus === 'ONLINE'
                                  ? 'bg-green-100 text-green-800'
                                  : node.nodeStatus === 'OFFLINE'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-orange-100 text-orange-800';
                              })()}`}
                            >
                              <div
                                className={`w-2 h-2 rounded-full mr-1.5 ${(() => {
                                  return node.nodeStatus === 'ONLINE'
                                    ? 'bg-green-500'
                                    : node.nodeStatus === 'OFFLINE'
                                    ? 'bg-red-500'
                                    : 'bg-orange-500';
                                })()}`}
                              />
                              {node.nodeStatus === 'ONLINE'
                                ? 'Online'
                                : node.nodeStatus === 'OFFLINE'
                                ? 'Offline'
                                : 'Disconnected'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {node.latency_ms.toFixed(2)} ms
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {node.version || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {node.perc_uptime !== undefined ? `${node.perc_uptime}%` : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {node.lastUpdated.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Dialogue d'édition */}
      <Transition appear show={openEditUnit} as={Fragment}>
        <HeadlessUIDialog
          as="div"
          className="relative z-[1100]"
          onClose={() => setOpenEditUnit(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <HeadlessUIDialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-2xl transition-all">
                  {/* Header */}
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    <HeadlessUIDialog.Title
                      as="h3"
                      className="text-[18px] font-semibold text-[#181D27]"
                    >
                      Settings
                    </HeadlessUIDialog.Title>
                    <button
                      onClick={() => setOpenEditUnit(false)}
                      className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  {/* Content */}
                  <div className="px-6 py-5 max-h-[60vh] overflow-y-auto">
                    <CommonFields
                      name={newUnit.name || ''}
                      storageType={newUnit.storageType || ApiTypes.StorageType.S3}
                      validationErrors={validationErrors}
                      isEdit={true}
                      onChange={(field, value) => {
                        setNewUnit((prev) => ({ ...prev, [field]: value }));
                        setValidationErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors[field];
                          return newErrors;
                        });
                      }}
                    />

                    {/* API Key Selection for Edit */}
                    <div className="mb-2 mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        API Key
                      </label>
                      <div className="space-y-3">
                        <div className="flex items-center">
                          <input
                            type="radio"
                            id="edit-new-api-key"
                            name="edit-api-key-mode"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                            checked={!newUnit.apiKeyId}
                            onChange={() => setNewUnit((prev) => ({ ...prev, apiKeyId: undefined }))}
                          />
                          <label htmlFor="edit-new-api-key" className="ml-3 block text-sm font-medium text-gray-700">
                            Configure new credentials
                          </label>
                        </div>

                        {availableApiKeys.length > 0 && (
                          <div>
                            <div className="flex items-center">
                              <input
                                type="radio"
                                id="edit-existing-api-key"
                                name="edit-api-key-mode"
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                checked={!!newUnit.apiKeyId}
                                onChange={() => {
                                  const suitableKey = (unit as any)?.apiKeyId
                                    ? availableApiKeys.find(k => k.id === (unit as any).apiKeyId)
                                    : availableApiKeys.find(k => k.provider === newUnit.storageType) || availableApiKeys[0];
                                  if (suitableKey) {
                                    setNewUnit((prev) => ({ ...prev, apiKeyId: suitableKey.id }));
                                  }
                                }}
                              />
                              <label htmlFor="edit-existing-api-key" className="ml-3 block text-sm font-medium text-gray-700">
                                Use existing API key
                              </label>
                            </div>
                            
                            {newUnit.apiKeyId && (
                              <div className="mt-2 ml-7">
                                <select
                                  value={newUnit.apiKeyId}
                                  onChange={(e) => setNewUnit((prev) => ({ ...prev, apiKeyId: e.target.value }))}
                                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md shadow-sm border"
                                >
                                  {availableApiKeys.map((key) => (
                                    <option key={key.id} value={key.id}>
                                      {key.provider} - {key.key.substring(0, 10)}... (Created {new Date(key.createdAt).toLocaleDateString()})
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {!newUnit.apiKeyId && (
                      <StorageTypeFields
                        storageType={newUnit.storageType || ApiTypes.StorageType.S3}
                        s3Fields={s3Fields}
                        gcsFields={gcsFields}
                        azureFields={azureFields}
                        validationErrors={validationErrors}
                        isEdit={true}
                        onChange={(field, value) => {
                          switch (newUnit.storageType) {
                            case ApiTypes.StorageType.S3:
                              setS3Fields((prev) => ({ ...prev, [field]: value }));
                              break;
                            case ApiTypes.StorageType.GCS:
                              setGcsFields((prev) => ({ ...prev, [field]: value }));
                              break;
                            case ApiTypes.StorageType.BLOB:
                              setAzureFields((prev) => ({ ...prev, [field]: value }));
                              break;
                          }

                          setValidationErrors((prev) => {
                            const newErrors = { ...prev };
                            delete newErrors[field];
                            return newErrors;
                          });
                        }}
                      />
                    )}
                  </div>

                  {/* Footer */}
                  <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50">
                    <button
                      onClick={() => setOpenEditUnit(false)}
                      className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleEditUnit}
                      className="px-5 py-2.5 text-sm font-semibold text-white bg-[#617BFF] rounded-lg hover:bg-[#5068e6] transition-colors shadow-sm"
                    >
                      Save Changes
                    </button>
                  </div>
                </HeadlessUIDialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </HeadlessUIDialog>
      </Transition>

      {/* Delete Confirmation Dialog */}
      <Transition appear show={deleteDialogOpen} as={Fragment}>
        <HeadlessUIDialog as="div" className="relative z-50" onClose={() => setDeleteDialogOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <HeadlessUIDialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all border-l-4 border-red-500">
                  <div className="flex items-center mb-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mr-4">
                      <DeleteIcon sx={{ color: '#EF4444', fontSize: 24 }} />
                    </div>
                    <div>
                      <HeadlessUIDialog.Title className="text-lg font-semibold text-gray-900">
                        Delete Storage Unit
                      </HeadlessUIDialog.Title>
                    </div>
                  </div>
                  <div className="mt-3 mb-6">
                    <p className="text-sm text-gray-600">
                      Are you sure you want to delete the storage unit{' '}
                      <span className="font-semibold text-gray-900">{unit?.name}</span>?
                      This action cannot be undone.
                    </p>
                  </div>
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setDeleteDialogOpen(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={async () => {
                        if (!unit) return;
                        try {
                          const response = await withTokenRefresh(() => apiClient.deleteStorageBucket(unit.id));
                          if (response.success) {
                            toastSuccess('Storage unit deleted successfully');
                            setDeleteDialogOpen(false);
                            navigate('/cloud-storage');
                          } else {
                            toastError((response as any).message || 'Failed to delete storage unit');
                          }
                        } catch (error) {
                          console.error('[StorageUnitDetails] Error deleting unit:', error);
                          toastError('Failed to delete storage unit');
                        }
                      }}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </HeadlessUIDialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </HeadlessUIDialog>
      </Transition>
    </div>
  );
}

export default StorageUnitDetails;
